import { strTo12Hours, strTo24Hours } from "./date";
import session from "../services/session.service";
import customerService from "../services/customer.service";

export const getNextBoxNumbers = async (type, startBox, boxNumber, turn = false, count = 3) => {
  const requestBody = {
    type: String(type),
    start_box: Number(startBox),
    box_number: Number(boxNumber),
    turn: Boolean(turn),
    count: Math.min(Math.max(Number(count), 1), 10)
  };

  const response = await customerService.getNextBoxNumbers(requestBody);
  
  if (response && response.status === 200) {
    return response.data.next_box_numbers || [];
  }
  
  return [];
};

export const holeToBoxMappings = {
  1: 1,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
  7: 7,
  8: 8,
  9: 9,
  10: 22,
  11: 12,
  12: 13,
  13: 14,
  14: 15,
  15: 16,
  16: 17,
  17: 18,
  18: 19,
};
export const statusText = {
  created: 'Created',
  'in-progress': 'In Progress',
  completed: 'Completed'
};

/** Walker/Cart from API (`transport_mode`); only GPS games may show W/C — non-GPS responses use null. */
export const getTransportDisplay = (game) => {
  if (!game || game.tracker_type !== 'gps') return '';
  const m = game.transport_mode;
  if (m === 'W' || m === 'w') return 'W';
  if (m === 'C' || m === 'c') return 'C';
  return '';
};

export const sortGames = (games, sortConfig) => {
  const { key, direction } = sortConfig;

  const parseValue = (val) => {
    if (!val) return null;

    // If it's a full datetime string
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(val)) {
      return new Date(val.replace(" ", "T"));
    }

    // If it's a 12-hour time string like '8:10 pm'
    if (/^\d{1,2}:\d{2} ?[ap]m$/i.test(val)) {
      const date = new Date();
      const [time, modifier] = val.toLowerCase().split(" ");
      let [hours, minutes] = time.split(":").map(Number);
      if (modifier === "pm" && hours !== 12) hours += 12;
      if (modifier === "am" && hours === 12) hours = 0;
      date.setHours(hours, minutes, 0, 0);
      return date;
    }

    // If it's a 24-hour time string like '15:12'
    if (/^\d{1,2}:\d{2}$/.test(val)) {
      const date = new Date();
      const [hours, minutes] = val.split(":").map(Number);
      date.setHours(hours, minutes, 0, 0);
      return date;
    }

    return val; // fallback for text/number fields
  };

  const sortedData = [...games].sort((a, b) => {
    let valA = a[key];
    let valB = b[key];

    // Special handling for tracker_number - extract numeric part if it exists
    if (key === 'tracker_number') {
      const numA = parseInt(String(valA || '').match(/^\d+/)?.[0] || '999999');
      const numB = parseInt(String(valB || '').match(/^\d+/)?.[0] || '999999');
      // If both have numbers, compare them; otherwise fall back to string comparison
      if (!isNaN(numA) && !isNaN(numB)) {
        return direction === "asc" ? numA - numB : numB - numA;
      }
    }

    // transport_mode: W | C | null — meaningful only for GPS trackers
    if (key === 'transport_mode') {
      const rank = (game) => {
        if (game.tracker_type !== 'gps') return -1;
        const raw = game.transport_mode;
        if (raw === 'W' || raw === 'w') return 2;
        if (raw === 'C' || raw === 'c') return 1;
        return 0;
      };
      valA = rank(a);
      valB = rank(b);
      if (valA < valB) return direction === 'asc' ? -1 : 1;
      if (valA > valB) return direction === 'asc' ? 1 : -1;
      return 0;
    }

    // Special handling for last_hole_played - can be number or "Turn"
    if (key === 'last_hole_played') {
      // Convert "Turn" to 9.5 for sorting purposes (between hole 9 and 10)
      const convertHole = (val) => {
        if (!val || val === '' || val === undefined) return -1;
        if (String(val).toLowerCase() === 'turn') return 9.5;
        return Number(val);
      };
      valA = convertHole(valA);
      valB = convertHole(valB);
      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    }

    valA = parseValue(valA);
    valB = parseValue(valB);
    
    if (valA < valB) return direction === "asc" ? -1 : 1;
    if (valA > valB) return direction === "asc" ? 1 : -1;

    // Same course: order by start time (earliest first, no start time last), not by game creation order
    if (key === 'club_name') {
      const timeA = parseValue(a.actual_time);
      const timeB = parseValue(b.actual_time);
      if (!timeA || !timeB) return (timeA ? 0 : 1) - (timeB ? 0 : 1);
      return timeA - timeB;
    }
    return 0;
  });

  return sortedData;
};
export const boxToHoleMappings = {
  1:  1,
  2:  2,
  3:  3,
  4:  4,
  5:  5,
  6:  6,
  7:  7,
  8:  8,
  9:  9,
  10: 'Turn',
  11: 10,
  22: 10,
  12: 11,
  13: 12,
  14: 13,
  15: 14,
  16: 15,
  17: 16,
  18: 17,
  19: 18,
  23: 1
};

export const getMinutesDifference = (d1, d2) => {
  const date1 = new Date(d1);
  const date2 = new Date(d2);
  
  // Get the difference in milliseconds
  const diffInMs = Math.abs(date2 - date1);
  
  // Convert to minutes
  return diffInMs / (1000 * 60);
};
export const getLocalDate = (days = 0) => {
  const date = new Date(Date.now() + days*86400000);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based, so add 1
  const day = String(date.getDate()).padStart(2, "0");
  return  `${year}-${month}-${day}`;
}

const compareTeeTimes = (time1, time2) => {
  if (!time1 || !time2) return 0;
  try {
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    return minutes1 - minutes2;
  } catch {
    return 0;
  }
};


export const isHeldUpByAnotherGroup = (currentGame, allGames) => {
  if (currentGame.pace >= 0) return false;
  if (!currentGame.current_box_number || !currentGame.tee_time) return false;
  
  const activeGames = allGames.filter(
    (g) =>
      g.id !== currentGame.id &&
      g.status === 'in-progress' &&
      g.current_box_number &&
      g.tee_time &&
      Number(g.club_id) === Number(currentGame.club_id) &&
      (!currentGame.course_id || g.course_id === currentGame.course_id)
  );

  if (activeGames.length === 0) return false;

  const currentBoxNumber = currentGame.current_box_number;
  const currentTeeTime = currentGame.tee_time;

  const nextBox = currentGame.next_box_number;
  
  if(!nextBox) {
    return false;
  }
  
  for (const otherGame of activeGames) {
    const otherBoxNumber = otherGame.current_box_number;
    const otherTeeTime = otherGame.tee_time;
    
    if (otherBoxNumber === currentBoxNumber || (nextBox && otherBoxNumber === nextBox)) {
      if (compareTeeTimes(otherTeeTime, currentTeeTime) < 5) {
        if(otherGame.pace < 0 && currentGame.pace < 0) {
          if(otherGame.pace < currentGame.pace) {
            return true;
          } else {
            currentGame.relativePace = currentGame.pace - otherGame.pace;
            return false;
          }
        }
      }
    }
  }
  return false;
};

export const isHoldingUpOthers = (currentGame, allGames) => {
  if (currentGame.pace >= 0) return false;
  if (!currentGame.current_box_number || !currentGame.tee_time) return false;
  
  const activeGames = allGames.filter(
    (g) =>
      g.id !== currentGame.id &&
      g.status === 'in-progress' &&
      g.current_box_number &&
      g.tee_time &&
      Number(g.club_id) === Number(currentGame.club_id) &&
      (!currentGame.course_id || g.course_id === currentGame.course_id)
  );

  if (activeGames.length === 0) return false;

  const currentBoxNumber = currentGame.current_box_number;
  const currentTeeTime = currentGame.tee_time;
  const lastBoxNumber = currentGame.last_box_number;

  for (const otherGame of activeGames) {
    const otherBoxNumber = otherGame.current_box_number;
    const otherTeeTime = otherGame.tee_time;
    
    if (otherBoxNumber === currentBoxNumber || lastBoxNumber == otherBoxNumber) {
      if (compareTeeTimes(otherTeeTime, currentTeeTime) > 5) {
        if(currentGame.pace < 0) {
          return true;
        }
      }
    }
  }
  
  return false;
};

export const getPaceColor = async (game = null, allGames = []) => {
  const heldUp = isHeldUpByAnotherGroup(game, allGames);
  const holdingUp = isHoldingUpOthers(game, allGames);
  const pace = game.relativePace ? game.relativePace : game.pace;
  if (pace >= 0) return 'green';
  if (heldUp) {
    return 'blue';
  } else {
    if (holdingUp) {
      if(pace > -5) {
        return 'yellow'
      }
      return 'red';
    } else {
      return 'orange';
    }
  }
}

export const calculateGameProperties = (boxTimeList, gameResponse, selectedDate, options = {}) => {
  if(boxTimeList.length) {
    const existTurn =
      options.clubTurn !== undefined && options.clubTurn !== null
        ? Boolean(options.clubTurn)
        : session.get('clubInfo')?.existTurn || false;

    const gamesMap = gameResponse?.map((game, gameIndex) => {
      const {box_entries_sorted: box_entries} = game;
      let cumulative = 0;
      
      game.club_turn = existTurn;
            
      if(box_entries.length) {
        if(box_entries.length >=2) {
          game.current_box_number = box_entries[box_entries.length - 1]?.box_number;
          game.last_box_number = box_entries[box_entries.length - 2]?.box_number;
          game.last_hole_played = boxToHoleMappings[box_entries[box_entries.length - 2]?.box_number];
          game.time_through_last_hole = box_entries[box_entries.length - 1]?.entry_time?.split(" ")[1];
          game.time_through_last_hole_org = box_entries[box_entries.length - 1]?.entry_time;
        }
        game.total_holes_played = box_entries.length - 1;
        let actualTotal = 0;
        let standardTotal = 0;
        let carry = 0;
        box_entries.forEach((boxEntry, boxIndex) => {
          const standard = boxTimeList.find((bt) => (bt.box_number == boxEntry.box_number) || (bt.box_number == 11 && boxEntry.box_number == 22) || (bt.box_number == 1 && boxEntry.box_number == 23))?.box_time;
          boxEntry.standard = standard;
          if(boxEntry.box_number !== 20) {
            standardTotal += standard;
          }
          if(boxIndex == 0 && boxEntry.entry_time) {
            let date = new Date(boxEntry.entry_time);
            if([1,22].includes(boxEntry.box_number)) {
              date = new Date(date.getTime() - 3 * 60 * 1000);
            }
            const pad = (n) => n.toString().padStart(2, '0');
            
            game.actual_time = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
            `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
          }
          if(boxEntry.entry_time && boxEntry.exit_time) {
            if(boxEntry.box_number === 10) {
              game.total_holes_played = game.total_holes_played - 1; //to not count box 10/turn as a hole.
            }
            boxEntry.timeTaken = getMinutesDifference(boxEntry.entry_time, boxEntry.exit_time);
            if(boxIndex == 0 && [1,22].includes(boxEntry.box_number)) {
              boxEntry.timeTaken += 3;
            }
            if(carry) {
              boxEntry.timeTaken += carry;
            }
            const roundedTimeTaken = Math.round(boxEntry.timeTaken);
            carry = boxEntry.timeTaken - roundedTimeTaken;
            boxEntry.timeTaken = roundedTimeTaken;
            actualTotal += boxEntry.timeTaken;
            if(standard !== undefined) {
              const difference = standard - boxEntry.timeTaken;
              cumulative += difference;
              boxEntry.variance = difference;
              boxEntry.cumulative = cumulative;
              boxEntry.variance = boxEntry.variance < 0 ? `(${boxEntry.variance * -1})` : `${boxEntry.variance}`
              boxEntry.cumulative = boxEntry.cumulative < 0 ? `(${boxEntry.cumulative * -1})` : `${boxEntry.cumulative}`
            }
          }
        })
        game.actualTotal = Math.round(actualTotal);
        game.standardTotal = Math.round(standardTotal);
      }
      if(game.time_through_last_hole) {
        game.time_through_last_hole = strTo12Hours(game.time_through_last_hole);
      }
      game.start_hole = boxToHoleMappings[game.start_box];
      game.pace = Math.round(cumulative);
      if(game.last_lat_long) {
        try{
          game.last_lat_long = JSON.parse(game.last_lat_long);
        } catch {
          game.last_lat_long = [];
        }
      }
      game.actual_time_org = game.actual_time;
      if(selectedDate !== `history:${getLocalDate()}` && game.actual_time) {
        game.actual_time = strTo12Hours(game.actual_time?.split(" ")[1]);
      }
      game.pace_formatted = game.pace < 0 ? `(${game.pace * -1 })` : `${game.pace}`;
      game['#'] = gameIndex + 1;
      if (game.auto_game === 1 && box_entries.length < 10) {
        game.display_type = '';
      } else {
        game.display_type = game.type;
      }
      let totalScore = 0;

      // Loop through possible players (1–4)
      for (let i = 1; i <= 4; i++) {
        const scoreData = game.scores?.[i];
        if (scoreData) {
          const scoreValue = scoreData.score ?? "";
          const scoreTime = scoreData.gameData?.currentTimeStr || "";
          const scoreLastBox = scoreData.gameData?.lastBoxNumber || null;
          const scoreLastHole = scoreLastBox ? boxToHoleMappings[scoreLastBox] : "";
          game[`score${i}`] = scoreValue;
          game[`score${i}Time`] = scoreTime;
          game[`score${i}Pace`] = scoreData.gameData?.pace;
          game[`score${i}LastHole`] = scoreLastHole;
          game[`score${i}BehindPace`] = scoreData.behindPace;
          game[`score${i}IsNextGameInFront`] = scoreData.isNextGameInFront ?? false;
          game[`score${i}IsPrevGameBehind`] = scoreData.isPrevGameBehind ?? false;
          if(scoreData.nexGameData) {
            const id = scoreData.nexGameData.id;
            const teeTime = gameResponse.find((game) => game.id === id)?.tee_time;
            game[`score${i}GFTeeTime`] = teeTime;
            const nextGameLastBox = scoreData.nexGameData?.lastBoxNumber || null;
            const nextGameLastHole = nextGameLastBox ? boxToHoleMappings[nextGameLastBox] : "";            
            game[`score${i}GF`] = nextGameLastHole;
            game[`score${i}GFP`] = scoreData.nexGameData?.pace;
            
          } else {
            game[`score${i}GF`] = "";
            game[`score${i}GFP`] = "";
            game[`score${i}GFTeeTime`] = "";
          }
          if(scoreData.prevGameData) {
            const id = scoreData.prevGameData.id;
            const teeTime = gameResponse.find((game) => game.id === id)?.tee_time || '';
            game[`score${i}GBTeeTime`] = teeTime;
            const prevGameLastBox = scoreData.prevGameData?.lastBoxNumber || null;
            const prevGameLastHole = prevGameLastBox ? boxToHoleMappings[prevGameLastBox] : "";            
            game[`score${i}GB`] = prevGameLastHole;
            game[`score${i}GBP`] = scoreData.prevGameData?.pace;

          } else {
            game[`score${i}GB`] = "";
            game[`score${i}GBP`] = "";
            game[`score${i}GBTeeTime`] = "";
          }
          // Add to total score (if numeric)
          if (!isNaN(Number(scoreValue))) {
            totalScore += Number(scoreValue);
          }
        } else {
          game[`score${i}`] = "";
          game[`score${i}Time`] = "";
          game[`score${i}GF`] = "";
          game[`score${i}GFP`] = "";
          game[`score${i}GFTeeTime`] = "";
          game[`score${i}GB`] = "";
          game[`score${i}GBP`] = "";
          game[`score${i}GBTeeTime`] = "";
          game[`score${i}Pace`] = "";
          game[`score${i}BehindPace`] = "";
          game[`score${i}IsNextGameInFront`] = false;
          game[`score${i}IsPrevGameBehind`] = false;
        }
      }

      game.totalScore = totalScore;

      return game;
    }) || [];
    return gamesMap;
  }
  return [];
}
// For Testing
// const Games = [ 
//   {id: 1, course_id: 1, tee_time: '8:00' , current_box_number: 6, last_box_number: 5, pace: -9, next_box_number: 7, status: 'in-progress'}, 
//   {id: 2, course_id: 1 , tee_time: '8:20' , current_box_number: 4 , next_box_number: 5,  last_box_number: 3, pace: -2, status: 'in-progress'},
//   {id: 3,  course_id: 1 , tee_time: '8:40' , current_box_number: 2,  next_box_number: 3, last_box_number: 1, pace: -4, status: 'in-progress'},
// ];

// console.log(getPaceColor(Games[1], Games));